import React, { useState, useEffect, useMemo } from 'react'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import {
    RootState,
    MediaType,
    AppMetaData,
    FloorplanData,
    FloorplanVariationData,
    ThumbSize,
    MediaTagData,
} from 'app/types'
import {
    Spinner,
    Media,
    MediaDialog,
    MediaTile,
    Input,
    Button,
    Message,
    IconButton,
} from 'components'
import { authenticateBrokerPortal, getApp } from 'services/appServices'
import { getMediaLink } from 'helpers/media'
import { icons } from 'app/constants'
import * as fnc from 'helpers/fnc'
import { logger } from 'helpers/logger'
import { applyTheming, setStandaloneApp } from 'actions/appActions'
import { updateProjectCurrent, addProjectMedia } from 'actions/appActions'

interface BrokerPortalPasswordProps {
    app: any
    onAuthenticated: () => void
}

function BrokerPortalPassword(props: BrokerPortalPasswordProps) {
    const { app, onAuthenticated } = props
    const [password, setPassword] = useState('')
    const [error, setError] = useState<string>(null)
    const [loading, setLoading] = useState(false)
    const media = useAppSelector((state: RootState) => state.app.media)
    const appMeta = app?.meta || app

    function handleSubmit() {
        if (!password || password.trim().length === 0) {
            setError('Please enter a password')
            return
        }
        setLoading(true)
        setError(null)
        authenticateBrokerPortal(appMeta.link, password)
            .then((result) => {
                if (result && result.authenticated) {
                    onAuthenticated()
                } else {
                    setError('Invalid password')
                }
            })
            .catch((err) => {
                logger.error('Broker portal auth error', err)
                setError('Invalid password. Please try again.')
            })
            .finally(() => {
                setLoading(false)
            })
    }

    return <div className="broker-portal-password fadeIn">
        <div className="broker-portal-password-card">
            {appMeta.logoMediaId && appMeta.logoMediaId in media && <div className="broker-portal-logo">
                <img src={getMediaLink(media[appMeta.logoMediaId].link, { app })} alt={appMeta.name} />
            </div>}
            <h2>Broker Portal</h2>
            <h3>{appMeta.name}</h3>
            <p className="broker-portal-welcome">
                Welcome to the {appMeta.name} broker portal. Please enter the access password to view project materials.
            </p>
            <div className="broker-portal-form">
                <Input
                    value={password}
                    onChange={setPassword}
                    onSubmit={handleSubmit}
                    placeholder="Enter password"
                    password={true}
                    showPasswordToggle={true}
                    disabled={loading}
                />
                <Button secondary onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Authenticating...' : 'Access Portal'}
                </Button>
            </div>
            {error && <Message error={error} />}
        </div>
    </div>
}

interface BrokerPortalContentProps {
    appMeta: AppMetaData
    appData: any
    themeId?: string
    colorSchemeId?: string
}

function getMediaTypeBadge(mediaItem: any): { icon: string, label: string } {
    if (!mediaItem) return { icon: 'fas fa-file', label: 'File' }
    if (mediaItem.mediaTypeId === MediaType.File) {
        const isPdf = mediaItem.link?.toLowerCase().endsWith('.pdf')
        return { icon: isPdf ? 'fas fa-file-pdf' : 'fas fa-file', label: isPdf ? 'PDF' : 'File' }
    }
    if (mediaItem.mediaTypeId === MediaType.Image) {
        return { icon: 'fas fa-image', label: 'Image' }
    }
    if (mediaItem.mediaTypeId === MediaType.Video) {
        return { icon: 'fas fa-video', label: 'Video' }
    }
    if (mediaItem.mediaTypeId === MediaType.Tour) {
        return { icon: 'fas fa-vr-cardboard', label: 'Tour' }
    }
    return { icon: 'fas fa-file', label: 'File' }
}

function BrokerPortalFloorplansTab(props: { appMeta: AppMetaData, appData: any }) {
    // Set to true to render using MediaTile component instead of custom tiles
    // Custom tiles are working but MediaTile treats File types as table rows (asList=true)
    // so we keep custom tiles for proper broker portal display
    const USE_MEDIA_TILE = false

    const { appMeta, appData } = props
    const media = useAppSelector((state: RootState) => state.app.media)
    const floorplans: FloorplanData[] = appData?.floorplans || []
    const [searchQuery, setSearchQuery] = useState('')

    const brokerPortalFloorplans = floorplans.filter(
        (fp) => fp.includeBrokerPortal === true || (fp.includeBrokerPortal as any) === 1
    )

    const filteredFloorplans = brokerPortalFloorplans.filter((fp) => {
        const query = searchQuery.toLowerCase().trim()
        if (!query) return true
        return fp.name.toLowerCase().includes(query)
    })

    function isExternalUrl(link: string): boolean {
        return link?.startsWith('http://') || link?.startsWith('https://') || link?.startsWith('//')
    }

    function handleMediaClick(mediaItem: any) {
        if (!mediaItem || !mediaItem.link) return

        if (isExternalUrl(mediaItem.link)) {
            window.open(mediaItem.link, '_blank').focus()
        } else {
            // Match MediaDialog behavior: open in new window for viewing
            const mediaUrl = getMediaLink(mediaItem.link, { app: appData })
            window.open(mediaUrl, '_blank').focus()
        }
    }

    function getDownloadIcon(mediaItem: any): string {
        if (!mediaItem || !mediaItem.link) return icons.download
        if (isExternalUrl(mediaItem.link) || mediaItem.mediaTypeId === MediaType.Video) {
            return 'fas fa-external-link-alt'
        }
        return icons.download
    }

    if (brokerPortalFloorplans.length === 0) {
        return <Message type="info">
            <span>No floorplans available at this time.</span>
        </Message>
    }

    return <div className="broker-portal-floorplans fadeIn">
        <div className="broker-portal-search">
            <Input
                placeholder="Search floorplans..."
                value={searchQuery}
                onChange={(value) => setSearchQuery(value)}
                clearable
            />
        </div>
        {filteredFloorplans.length === 0 ? (
            <Message type="info">
                <span>No floorplans match your search.</span>
            </Message>
        ) : (
            filteredFloorplans.map((floorplan) => {
                return floorplan.variations.map((variation: FloorplanVariationData) => {
                    // Gather all media for this variation from multiple sources
                    const variationMediaItems: any[] = []
                    const seenIds = new Set<string>()
                    const debugInfo: any = {
                        variationId: variation.id,
                        variationName: variation.name,
                        mediaLinked: [],
                        mediaNotFound: [],
                    }

                    // Helper to add media if not already added
                    const addMediaIfExists = (mediaId: string, source: string) => {
                        if (!mediaId) {
                            debugInfo.mediaNotFound.push({ source, mediaId: 'null/undefined' })
                            return
                        }
                        if (seenIds.has(mediaId)) {
                            return
                        }
                        const mediaItem = media?.[mediaId]
                        if (mediaItem) {
                            variationMediaItems.push(mediaItem)
                            seenIds.add(mediaId)
                            debugInfo.mediaLinked.push({ 
                                source, 
                                mediaId, 
                                name: mediaItem.name,
                                type: mediaItem.mediaTypeId 
                            })
                        } else {
                            debugInfo.mediaNotFound.push({ source, mediaId })
                        }
                    }

                    // 1. Add variation media (from floorplan_variation_media table)
                    if (variation.media && Array.isArray(variation.media) && variation.media.length > 0) {
                        variation.media.forEach((vm: any) => {
                            addMediaIfExists(vm.mediaId, `variation.media[${variation.media.indexOf(vm)}]`)
                        })
                    }

                    // 2. Add floorplan images (from floorplan_variation_floorplan table)
                    if (variation.floorplans && Array.isArray(variation.floorplans) && variation.floorplans.length > 0) {
                        variation.floorplans.forEach((vf: any, idx: number) => {
                            addMediaIfExists(vf.mediaId, `variation.floorplans[${idx}].mediaId`)
                        })
                    }

                    // 3. Add pdf media
                    if (variation.pdfMediaId) {
                        addMediaIfExists(variation.pdfMediaId, 'variation.pdfMediaId')
                    }

                    if (variationMediaItems.length === 0) {
                        console.warn('[BrokerPortalFloorplansTab] No media found for variation:', debugInfo)
                        return null
                    }

                    console.log('[BrokerPortalFloorplansTab] Media found for variation:', debugInfo)
                    console.log('[BrokerPortalFloorplansTab] Media items to render:', variationMediaItems.map(m => ({ 
                        id: m.id, 
                        name: m.name, 
                        link: m.link, 
                        mediaTypeId: m.mediaTypeId,
                        appId: m.appId 
                    })))

                    // Build title as "Floorplan Name - Variation Name" if multiple variations
                    const cardTitle = floorplan.variations.length > 1 
                        ? `${floorplan.name} - ${variation.name}`
                        : floorplan.name

                    return <div key={variation.id} className="broker-portal-floorplan-group">
                        <div className="broker-portal-floorplan-header">
                            <div className="broker-portal-floorplan-title-section">
                                <h3 className="broker-portal-floorplan-name">{cardTitle}</h3>
                            </div>
                            <div className="broker-portal-floorplan-info">
                                {variation.sqft && <div className="info-item">
                                    <i className="fas fa-vector-square"></i>
                                    <div>
                                        <span className="info-label">Sq Ft</span>
                                        <span className="info-value">{variation.sqft.toLocaleString()}</span>
                                    </div>
                                </div>}
                                {variation.beds !== null && variation.beds !== undefined && <div className="info-item">
                                    <i className="fas fa-bed"></i>
                                    <div>
                                        <span className="info-label">Beds</span>
                                        <span className="info-value">{variation.beds}</span>
                                    </div>
                                </div>}
                                {variation.baths !== null && variation.baths !== undefined && <div className="info-item">
                                    <i className="fas fa-bath"></i>
                                    <div>
                                        <span className="info-label">Baths</span>
                                        <span className="info-value">{variation.baths}</span>
                                    </div>
                                </div>}
                                {variation.garage && <div className="info-item">
                                    <i className="fas fa-car"></i>
                                    <div>
                                        <span className="info-label">Garage</span>
                                        <span className="info-value">{variation.garage}</span>
                                    </div>
                                </div>}
                            </div>
                        </div>
                        <div className="broker-portal-variation-media tile-list">
                            {variationMediaItems.length > 0 ? (
                                !USE_MEDIA_TILE ? (
                                    // CUSTOM TILE RENDERING (WORKING) - Using MediaTile styles
                                    variationMediaItems.map((mediaItem) => {
                                        const badge = getMediaTypeBadge(mediaItem)
                                        // Use getMediaLink with thumbnail option for proper URL and sizing
                                        const thumbUrl = mediaItem.link 
                                            ? getMediaLink(mediaItem.link, { 
                                                app: appData,
                                                thumb: true,
                                                thumbSize: 256
                                              })
                                            : null

                                        const mediaDebugInfo = {
                                            mediaId: mediaItem.id,
                                            mediaName: mediaItem.name,
                                            mediaTypeId: mediaItem.mediaTypeId,
                                            link: mediaItem.link,
                                            appId: mediaItem.appId,
                                            appDataId: appData?.meta?.id,
                                            thumbUrl
                                        }
                                        console.log('[BrokerPortalFloorplansTab] Custom tile for:', mediaDebugInfo)

                                        return (
                                            <div 
                                                key={mediaItem.id} 
                                                className="tile show media-tile square"
                                                style={{ cursor: 'default', opacity: 1, position: 'relative' }}
                                            >
                                                <IconButton 
                                                    icon="fas fa-external-link-alt" 
                                                    onClick={(e) => { 
                                                        e.stopPropagation()
                                                        handleMediaClick(mediaItem) 
                                                    }}
                                                    className="media-tile-download"
                                                    large
                                                    tertiary
                                                    title="Open in new tab"
                                                />
                                                <IconButton 
                                                    icon={icons.download} 
                                                    onClick={(e) => { 
                                                        e.stopPropagation()
                                                        const mediaUrl = getMediaLink(mediaItem.link, { app: appData })
                                                        fnc.downloadURI(mediaUrl, mediaItem.name || 'download')
                                                    }}
                                                    className="media-tile-download"
                                                    large
                                                    tertiary
                                                    title="Download"
                                                />
                                                {thumbUrl && mediaItem.mediaTypeId === MediaType.Image && (
                                                    <img 
                                                        src={thumbUrl} 
                                                        alt={mediaItem.name}
                                                        className="media noselect show"
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                )}
                                                {mediaItem.mediaTypeId === MediaType.File && (
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        flexDirection: 'column', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center',
                                                        width: '100%',
                                                        height: '100%',
                                                        backgroundColor: '#f5f5f5'
                                                    }}>
                                                        <i className="fas fa-file-pdf" style={{ fontSize: '48px', marginBottom: '12px', color: '#d32f2f' }} />
                                                        <div style={{ fontSize: '12px', textAlign: 'center', color: '#666', padding: '0 10px' }}>
                                                            <span>{mediaItem.name}</span>
                                                        </div>
                                                    </div>
                                                )}
                                                {mediaItem.mediaTypeId === MediaType.Video && (
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center',
                                                        width: '100%',
                                                        height: '100%',
                                                        backgroundColor: '#000'
                                                    }}>
                                                        <i className="fas fa-play" style={{ fontSize: '48px', color: '#fff' }} />
                                                    </div>
                                                )}
                                                <div className="media-info noselect">
                                                    <span>{mediaItem.link || mediaItem.name}</span>
                                                </div>
                                            </div>
                                        )
                                    })
                                ) : (
                                    // MEDIA TILE RENDERING (WITH DEBUG)
                                    variationMediaItems.map((mediaItem) => {
                                        const badge = getMediaTypeBadge(mediaItem)
                                        const downloadIconValue = getDownloadIcon(mediaItem)
                                        const mediaTileProps = {
                                            app: { meta: appMeta }, // Provide app context
                                            builder: null,
                                            organization: null,
                                            media: mediaItem,
                                            onClick: () => handleMediaClick(mediaItem),
                                            selected: false,
                                            scrollTo: false,
                                            isDialog: false,
                                            listRef: null,
                                            draggable: false,
                                            onReorder: null,
                                            order: 0,
                                            name: mediaItem.name,
                                            showInfo: true,
                                            onEditName: null,
                                            selectIcon: false,
                                            aspect: true,
                                            square: false,
                                            toggleValue: false,
                                            onToggle: null,
                                            toggleLabel: '',
                                            debug: true,
                                            downloadIcon: downloadIconValue,
                                        }

                                        console.log('[BrokerPortalFloorplansTab] MediaTile props:', {
                                            mediaId: mediaItem.id,
                                            mediaName: mediaItem.name,
                                            mediaTypeId: mediaItem.mediaTypeId,
                                            appProvided: !!mediaTileProps.app,
                                            builderProvided: !!mediaTileProps.builder,
                                            organizationProvided: !!mediaTileProps.organization,
                                            mediaProvided: !!mediaTileProps.media,
                                            onClickProvided: !!mediaTileProps.onClick,
                                            downloadIconProvided: !!mediaTileProps.downloadIcon,
                                            debug: mediaTileProps.debug,
                                        })

                                        return (
                                            <MediaTile
                                                key={mediaItem.id}
                                                {...mediaTileProps}
                                            />
                                        )
                                    })
                                )
                            ) : (
                                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                    No media items
                                </div>
                            )}
                        </div>
                    </div>
                })
            })
        )}
    </div>
}

function BrokerPortalContent(props: BrokerPortalContentProps) {
    const { appMeta, appData, themeId, colorSchemeId } = props
    const dispatch = useAppDispatch()
    const organization = useAppSelector((state: RootState) => state.app.organization)
    const config = useAppSelector((state: RootState) => state.app.config)
    const media = useAppSelector((state: RootState) => state.app.media)
    const [activeTab, setActiveTab] = useState<string>('floorplans')

    // Apply theming for this app
    useEffect(() => {
        const appObj = { themeId, colorSchemeId }
        applyTheming(() => ({ app: { config } }), dispatch, organization, appObj)
    }, [themeId, colorSchemeId, organization?.id, dispatch])

    function isExternalUrl(link: string): boolean {
        return link?.startsWith('http://') || link?.startsWith('https://') || link?.startsWith('//')
    }

    function getDownloadIcon(mediaItem: any): string {
        if (!mediaItem || !mediaItem.link) return icons.download
        
        // External URLs and videos get an "open in new" icon
        if (isExternalUrl(mediaItem.link) || mediaItem.mediaTypeId === MediaType.Video) {
            return 'fas fa-external-link-alt'
        }
        
        // Local files get download icon
        return icons.download
    }

    function handleMediaClick(mediaItem: any) {
        if (!mediaItem || !mediaItem.link) return

        if (isExternalUrl(mediaItem.link)) {
            // Open external URLs in new tab
            window.open(mediaItem.link, '_blank').focus()
        } else {
            // Open internal media files in new tab
            const mediaUrl = getMediaLink(mediaItem.link, { app: appData })
            window.open(mediaUrl, '_blank').focus()
        }
    }

    // Check if there are any media items (all media is visible in broker portal tabs)
    const brokerPortalMedia = Object.values(media).length > 0

    // Check if there are broker portal floorplans
    const floorplans: FloorplanData[] = appData?.floorplans || []
    const hasFloorplans = floorplans.some((fp) => fp.includeBrokerPortal === true || (fp.includeBrokerPortal as any) === 1)

    // Get media tags that have include_broker_portal enabled
    const brokerPortalTags: MediaTagData[] = useMemo(() => {
        const mediaTags: MediaTagData[] = appMeta?.mediaTags || []
        return mediaTags.filter((tag) => tag.includeBrokerPortal === true || (tag.includeBrokerPortal as any) === 1)
    }, [appMeta?.mediaTags])

    function toTitleCase(str: string): string {
        return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
    }
    console.log("[BrokerPortalContent] Tag debug - brokerPortalTags:", brokerPortalTags)

    return <div className="broker-portal-content fadeIn">
        <div className="broker-portal-header">
            {appMeta.logoMediaId && appMeta.logoMediaId in media && <div className="broker-portal-logo">
                <img src={getMediaLink(media[appMeta.logoMediaId].link, { app: appData })} alt={appMeta.name} />
            </div>}
            <div>
                <p>Broker Portal</p>
                <h1>{appMeta.name}</h1>
            </div>
        </div>

        <div className="broker-portal-tabs">
            <Button
                className={`broker-portal-tab${activeTab === 'floorplans' ? ' selected' : ''}`}
                onClick={() => setActiveTab('floorplans')}
            >
                Floorplans
            </Button>
            {brokerPortalTags.map((tag) => (
                <Button
                    key={tag.id}
                    className={`broker-portal-tab${activeTab === `tag-${tag.id}` ? ' selected' : ''}`}
                    onClick={() => setActiveTab(`tag-${tag.id}`)}
                >
                    {toTitleCase(tag.name)}
                </Button>
            ))}
        </div>

        {activeTab === 'floorplans' && (
            <div className="broker-portal-floorplans-tab">
                <BrokerPortalFloorplansTab appMeta={appMeta} appData={appData} />
            </div>
        )}

        {brokerPortalTags.map((tag) => (
            activeTab === `tag-${tag.id}` && (
                <div key={tag.id} className="broker-portal-tag-media">
                    <MediaDialog
                        app={appData}
                        mediaTypeId={[MediaType.Image, MediaType.Video, MediaType.File]}
                        upload={false}
                        multi={false}
                        readonly={true}
                        noHeader={false}
                        initialTagFilter={tag.id}
                        onMediaClick={handleMediaClick}
                        getDownloadIcon={getDownloadIcon}
                        fileTableLayout={false}
                        showTitle={false}
                        showMediaTypeToggle={false}
                        showTagDropdown={false}
                        showTagBadges={false}
                        brokerPortalMode={true}
                    />
                </div>
            )
        ))}
    </div>
}

interface BrokerPortalPageProps {
    appLink: string
}

export function BrokerPortalPage(props: BrokerPortalPageProps) {
    const { appLink } = props
    const dispatch = useAppDispatch()
    const organization = useAppSelector((state: RootState) => state.app.organization)
    const [authenticated, setAuthenticated] = useState(false)
    const [app, setApp] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string>(null)

    // Fetch the app data by link
    useEffect(() => {
        setLoading(true)
        setError(null)
        // Set standalone app mode so organization options don't show in topbar
        dispatch(setStandaloneApp(true))
        getApp(appLink, null, false, false, false)
            .then((appData) => {
                if (appData && appData.meta) {
                    console.log('[BrokerPortalPage] App loaded:', appData.meta.name)
                    console.log('[BrokerPortalPage] Total media returned:', appData.media?.length || 0)
                    if (appData.media && appData.media.length > 0) {
                        const mediaByType = {}
                        const mediaByTag = {}
                        appData.media.forEach((m: any) => {
                            if (!mediaByType[m.mediaTypeId]) mediaByType[m.mediaTypeId] = 0
                            mediaByType[m.mediaTypeId]++
                            if (m.mediaTagId) {
                                if (!mediaByTag[m.mediaTagId]) mediaByTag[m.mediaTagId] = []
                                mediaByTag[m.mediaTagId].push({ id: m.id, name: m.name, mediaTypeId: m.mediaTypeId })
                            }
                        })
                        console.log('[BrokerPortalPage] Media breakdown by type:', mediaByType)
                        console.log('[BrokerPortalPage] Media breakdown by tag:', mediaByTag)
                    }
                    setApp(appData) // Store full app data for context in getMediaLink
                    // Update Redux state so Logo component can access the app
                    dispatch(updateProjectCurrent(appData))
                    if (appData.media && appData.media.length > 0) {
                        dispatch(addProjectMedia(appData.media))
                    }
                    // Apply theming for the fetched app
                    applyTheming(() => ({ app: { config: {} } }), dispatch, organization, appData.meta)
                } else {
                    setError(`Project not found: ${appLink}`)
                }
            })
            .catch((err) => {
                logger.error('Failed to load broker portal app', err)
                setError(`Failed to load project: ${appLink}`)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [appLink, dispatch, organization?.id])

    if (loading) {
        return <div className="broker-portal-page">
            <Spinner />
        </div>
    }

    if (error || !app) {
        return <div className="broker-portal-page">
            <Message type="error" message={error || `Project not found: ${appLink}`} />
        </div>
    }

    return <div id="broker-portal-page" className="broker-portal-page">
        {!authenticated
            ? <BrokerPortalPassword app={app} onAuthenticated={() => setAuthenticated(true)} />
            : <BrokerPortalContent appMeta={app.meta} appData={app} themeId={app.meta.themeId} colorSchemeId={app.meta.colorSchemeId} />
        }
    </div>
}
